import { useEffect } from 'react';
import { Container } from '@/components/design-system/container';
import { Button } from '@/components/design-system/button';
import { Badge } from '@/components/design-system/badge';
import { Skeleton } from '@/components/design-system/skeleton';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/design-system/pagination';
import { ProductCard } from '@/components/hoc/ProductCard';
import { useProducts } from '@/hooks/useProducts';
import { useCategories } from '@/hooks/useCategories';
import { BANNER_IMAGE_URL } from '@/utils/constants';

export const HomePage = () => {
  const {
    products,
    isLoading: productsLoading,
    page,
    totalPages,
    selectedCategoryId,
    handleCategoryFilter,
    handlePageChange,
  } = useProducts();

  const { categories, isLoading: categoriesLoading } = useCategories();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Banner */}
      <section className="relative h-[400px] md:h-[500px] overflow-hidden bg-gradient-to-r from-primary/90 to-primary">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${BANNER_IMAGE_URL})` }}
        />
        <Container className="relative flex h-full flex-col items-center justify-center text-center space-y-6 px-6">
          <h1 className="text-4xl md:text-6xl font-bold text-primary-foreground drop-shadow-lg">
            Discover Timeless Elegance
          </h1>
          <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl drop-shadow">
            Curated home decor, lighting, and gifts to transform your space
          </p>
        </Container>
      </section>

      {/* Category Navigation */}
      <section className="border-b bg-card sticky top-0 z-10 shadow-sm">
        <Container className="py-4 px-6">
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <Button
              variant={selectedCategoryId === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleCategoryFilter(null)}
              className="whitespace-nowrap"
            >
              All Products
            </Button>
            {categoriesLoading ? (
              <>
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
                <Skeleton className="h-9 w-24" />
              </>
            ) : (
              categories.map((category) => (
                <Button
                  key={category.id}
                  variant={selectedCategoryId === category.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => handleCategoryFilter(category.id)}
                  className="whitespace-nowrap"
                >
                  {category.name}
                </Button>
              ))
            )}
          </div>
        </Container>
      </section>

      {/* Products Grid */}
      <section className="py-12">
        <Container className="px-6 space-y-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-3xl font-bold">
                {selectedCategoryId
                  ? categories.find((c) => c.id === selectedCategoryId)?.name || 'Products'
                  : 'All Products'}
              </h2>
              {!productsLoading && (
                <p className="text-muted-foreground mt-1">
                  Showing page {page} of {totalPages}
                </p>
              )}
            </div>
            {selectedCategoryId && (
              <Badge variant="secondary" className="text-sm">
                Filtered
              </Badge>
            )}
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="space-y-3">
                  <Skeleton className="aspect-square w-full" />
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-8 w-1/3" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <div className="text-6xl opacity-50">📦</div>
              <h3 className="text-xl font-semibold text-muted-foreground">No products found</h3>
              <p className="text-muted-foreground text-center max-w-md">
                {selectedCategoryId
                  ? 'Try selecting a different category or view all products.'
                  : 'Check back soon for new arrivals.'}
              </p>
              {selectedCategoryId && (
                <Button onClick={() => handleCategoryFilter(null)} variant="outline">
                  View All Products
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center pt-8">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => page > 1 && handlePageChange(page - 1)}
                          className={page === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>

                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (page <= 3) {
                          pageNum = i + 1;
                        } else if (page >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = page - 2 + i;
                        }

                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              onClick={() => handlePageChange(pageNum)}
                              isActive={page === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      })}

                      {totalPages > 5 && page < totalPages - 2 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => page < totalPages && handlePageChange(page + 1)}
                          className={page === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </Container>
      </section>
    </div>
  );
};
